// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import translate from "@insite/client-framework/Translate";
import { VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
// import React from "react"; // Foundation
import { css } from "styled-components";

/* Starter Custom Code - Start */
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { swatchListItemStyles, swatchListStyles } from "@store/Themes/common/mixins/swatch";
import { swatchProps } from "@store/Themes/common/props/swatch";
import useSwatchSelectedAccessibleText from "@toolkit/Framework/Common/Hooks/useSwatchSelectedAccessibleText";
/* Starter Custom Code - End */

// Foundation -->
import Slash from "@store/Library/Components/Icons/Slash";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useEffect } from "react";
// <-- Foundation

interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
    // Foundation
    disableClearSelection: boolean;
}

export interface ProductDetailsVariantButtonStyles {
    wrapper?: InjectableCss;
    swatchHeader?: InjectableCss;
    button?: ButtonPresentationProps;
    labelText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    // Starter Custom Code
    swatchList?: InjectableCss;
    swatchListItem?: InjectableCss;
    selectedButton?: ButtonPresentationProps;
    unselectedButton?: ButtonPresentationProps;
    // Foundation
    labelWrapper?: InjectableCss;
    buttonContainer?: InjectableCss;
    disabled?: InjectableCss;
}

export const variantButtonsStyles: ProductDetailsVariantButtonStyles = {
    disabled: {
        css: css`
            width: ${pxToRem(50)};
            height: ${pxToRem(50)};
            position: absolute;
            margin: ${pxToRem(5)};
            display: inline;
            svg {
                top: 0;
                width: 100%;
                height: 100%;
                opacity: 0.75;
            }
        `,
    },
    buttonContainer: {
        css: css`
            display: inline;
        `,
    },
    button: {
        css: css`
            margin: 5px;
            white-space: nowrap;
            overflow: hidden;
            height: ${pxToRem(50)};
            width: ${pxToRem(50)};
            padding: 0;
        `,
    },
    labelText: {
        css: css`
            font-size: 15px;
            font-weight: 600;
        `,
    },
    link: {
        color: "primary",
        css: css`
            margin: 10px 5px;
            margin-left: ${pxToRem(50)};
        `,
    },
    labelWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    wrapper: swatchProps.wrapper,
    swatchList: { css: swatchListStyles() },
    swatchListItem: { css: swatchListItemStyles() },
    selectedButton: { variant: "selected" as any },
    unselectedButton: { variant: "unselected" as any },
};

const styles = variantButtonsStyles;

const ProductDetailsVariantButton = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    // Foundation
    disableClearSelection,
}: Props) => {
    const swatchSelectedAccessibleText = useSwatchSelectedAccessibleText(variantTrait); // Starter Custom Code

    // Foundation -->
    const updateVariantSelectionSwatchGrid = (traitValueId?: string) => {
        updateVariantSelection(traitValueId ?? "", variantTrait.id);
    };

    useEffect(() => {
        if (variantTrait.traitValues && variantTrait.traitValues.length <= 1) {
            updateVariantSelectionSwatchGrid(variantTrait.traitValues[0].id);
        }
    }, []);
    // <-- Foundation

    return (
        <StyledWrapper key={variantTrait.id}>
            <StyledWrapper {...styles.labelWrapper}>
                <Typography {...styles.labelText}>
                    {variantTrait.nameDisplay}
                    {/* Starter Custom Code - Start */}
                    {swatchSelectedAccessibleText && (
                        <VisuallyHidden>{` ${swatchSelectedAccessibleText}`}</VisuallyHidden>
                    )}
                    {/* Starter Custom Code - End */}
                </Typography>
                {!disableClearSelection && (
                    <Link {...styles.link} onClick={() => updateVariantSelectionSwatchGrid("")}>
                        {translate("Clear Selection")}
                    </Link>
                )}
            </StyledWrapper>
            {variantTrait.traitValues?.map(traitValue => (
                // Foundation -->
                <StyledWrapper {...styles.buttonContainer} key={`${traitValue.id}`}>
                    {traitValue.isDisabled && (
                        <StyledWrapper {...styles.disabled}>
                            <Slash />
                        </StyledWrapper>
                    )}
                    <Button
                        {...styles.button}
                        variant={variantSelection[variantTrait.id] === traitValue.id ? "primary" : "secondary"}
                        buttonType={variantSelection[variantTrait.id] === traitValue.id ? "solid" : "outline"}
                        value={`${traitValue.id}`}
                        key={`${traitValue.id}`}
                        shape="rectangle"
                        disabled={traitValue.isDisabled}
                        onClick={event => updateVariantSelection(event.currentTarget.value, variantTrait.id)}
                    >
                        {traitValue.valueDisplay}
                    </Button>
                </StyledWrapper>
            ))}
        </StyledWrapper>
        // <-- Foundation
    );
};

export default ProductDetailsVariantButton;
